import { formatBytes, SECONDS_PER_MIN } from "utils/common";

const ASSET_NAME_MAX_LENGTH = 250;
const RICHLOAD_NAME_MAX_LENGTH = 244;

export function assetName(value){
	if(value === null || value === undefined || value === ""){
		// Checking if the value is empty should be a separate validator
		return;
	}

	if(/^[-a-zA-Z0-9~._]+$/.test(value) === false){
		return "Asset names are only allowed to contain alphanumerics and the following characters: - _ . ~";
	}

	if(value.length > ASSET_NAME_MAX_LENGTH){
		return `Asset names must be less than ${ASSET_NAME_MAX_LENGTH} characters long`;
	}
}

export function richloadName(value){
	/*
		Richload names allow backslashes, and have a shorter max length than other assets
		(because they append "/index.html" in the DB)
	*/
	if(value === null || value === undefined || value === ""){
		// Checking if the value is empty should be a separate validator
		return;
	}

	if(/^[-A-Za-z0-9._~\\]+$/.test(value) === false){
		return "Richload names are only allowed to contain alphanumerics and the following characters: - _ . ~ \\";
	}

	if(value.length > RICHLOAD_NAME_MAX_LENGTH){
		return `Richload names must be less than ${RICHLOAD_NAME_MAX_LENGTH} characters long`;
	}
}

/*
	Asset Validators

		These validators depend on the value being formatted to contain a `metadata` property, with the parameters
		necessary to validate.  e.g. `width` and `height` for validating dimensions.

		JCRM doesn't include the asset metadata in a Creative's `socialValues`, but it does include asset validation
		problems under `warnings`, which we display to the user when editing, so validators should be written to
		return `undefined` if the required metadata is missing and the client can't determine if it's valid or not.

		Helper functions to get these values are also provided in this file.
*/

export function exactDimensions({ width = 0, height = 0 }) {
	return (value) => {
		let { width: valueWidth = null, height: valueHeight = null } = value?.metadata || {};

		if(valueWidth === null || valueHeight === null){
			// There's no value, or we otherwise can't assess the dimensions to be able to validate
			return;
		}

		if (width !== valueWidth || height !== valueHeight) {
			return `Asset must be exactly ${width}x${height}px`;
		}
	};
}

export function dimensions({
	minWidth = 0,
	maxWidth = Infinity,
	minHeight = 0,
	maxHeight = Infinity,
}){
	return (value) => {
		let { width = null, height = null } = value?.metadata || {};

		if(width === null || height === null){
			// There's no value, or we otherwise can't assess the dimensions to be able to validate
			return undefined;
		}

		if(width < minWidth){
			return `${width} is below the minimum ${minWidth} width`;
		}
		if(width > maxWidth){
			return `${width} is above the maximum ${maxWidth} width`;
		}
		if(height < minHeight){
			return `${height} is below the minimum ${minHeight} height`;
		}
		if(height > maxHeight){
			return `${height} is above the maximum ${maxHeight} height`;
		}

		return undefined;
	};
}

export function filesize({ min = 0, max = Infinity }){
	// Values assumed to be in bytes
	return (value) => {
		//Some social formats, e.g. Google Demand Gen, have a filesize prop directly on the value, so we need to check for that too
		let size = value?.metadata?.filesize ?? value?.filesize ?? null; // filesize might be 0, so we nullish coalesce
		if(size === null){
			// There's no value, or we otherwise can't assess the filesize to be able to validate
			return undefined;
		}

		if(size < min){
			return `${formatBytes(size)} is below the minimum ${formatBytes(min)} filesize`;
		}
		if(size > max){
			return `${formatBytes(size)} is above the maximum ${formatBytes(max)} filesize`;
		}
		return undefined;
	};
}

export function aspectRatios(allowedRatios, tolerance = 0) {
	/*
		allowedRatios is expected to be an array of objects in the form:
		{ width, height }

		`tolerance` is expected to be a number between 0 and 1 representing how close to
		exact an asset's asset ratio must be to an allowed ratio in order to pass validation.

		e.g. A tolerance of 0.05 represents as 5% tolerance.
		For an aspect ratio of 1:1, this would mean the asset's width/height would
		need to be between 0.95 and 0.05, inclusive.
	*/
	return (value) => {
		if(!value?.metadata){
			// We can't assess the aspect ratio without metadata to be able to validate.
			return;
		}
		const { width = null, height = null } = value?.metadata ||  {};
		const ratio = width / height;

		const matched = allowedRatios.find((target) => {
			const targetRatio = target.width / target.height;
			const low = targetRatio * (1 - tolerance);
			const high = targetRatio * (1 + tolerance);
			return (ratio >= low) && (ratio <= high);
		});

		if(!matched){
			const allowedRatioStrings = allowedRatios.map(raw => `${raw.width}:${raw.height}`).join(", ");
			return `Aspect ratio must be one of: ${allowedRatioStrings}`;
		}
	};
}

function formatDuration(value) {
	let formatted = "";

	if (isNaN(value)) { return formatted; }

	value = parseInt(value);

	if (value > SECONDS_PER_MIN) {
		const mins = Math.floor(value / SECONDS_PER_MIN);
		value -= mins * SECONDS_PER_MIN;

		formatted += `${mins}m`;
	}

	formatted += ` ${value}s`;

	return formatted.trim();
}

/**
 * Duration validator that accepts optional min and max values in seconds. It returns a value that compares
 * these to the duration of some asset's metadata.
 *
 * Optionally, this function can accept specific labels for min or max (minLabel and maxLabel). This is for
 * specific instances where we do not want the error message formatted (e.g. 100s instead of 1m 40s).
 */
export function duration({ min = 0, max = Infinity, minLabel = formatDuration(min), maxLabel = formatDuration(max) }) {
	return (value) => {
		let valueDuration = value?.metadata?.duration ?? null; // duration might be 0, so we nullish coalesce
		if(valueDuration === null){
			// There's no value, or we otherwise can't assess the duration to be able to validate
			return undefined;
		}

		let formatted = formatDuration(valueDuration);

		if (valueDuration < min) {
			return `${formatted} is below the minimum ${minLabel} duration`;
		}

		if(valueDuration > max){
			return `${formatted} is above the maximum ${maxLabel} duration`;
		}

		return undefined;
	};
}


/*
	Validation Helpers

		In order to validate things like asset dimensions for files intended for upload, we have to
		load them into DOM elements, and then inspect their properties.

		These functions return Promises, which should resolve before updating the form value, which allows
		validation to stay simple and synchronous.

		e.g.
		```
			function ReduxFormCustomFileInput(props){
				let { onChange } = props.input;

				async function changeFile(e){
					let [file] = e.target.files;
					let src = URL.createObjectURL(file);

					try{
						let metadata = await getImageMetadata(src); // Note the `await` before calling `onChange`
						onChange({
							file,
							metadata,
						});
					} catch {
						onChange(null);
					}
				}

				return <input type="file" onChange={changeFile} />;
			}

			...

			function tooWide(value){
				let { width } = value.metadata || {};
				if(width > maxWidth){
					return "Too wide";
				}
				return undefined;
			}

			<ReduxFormCustomFileInput
				validate: [tooWide],
			/>
		```

		If it's a valid `src` for an <img/> or <video/>, these should work, but prefer to use these in scenarios where
		you're not waiting to download a large file, e.g. when you're using a file input and can build a blob or data URL
		that doesn't need to network.
*/

export function getImageMetadata(src){
	return new Promise((resolve, reject) => {
		let img = document.createElement("img");

		img.addEventListener("load", (e) => {
			resolve({
				width: e.target.naturalWidth,
				height: e.target.naturalHeight,
			});
		});

		img.addEventListener("error", (e) => {
			reject();
		});

		img.src = src;
	});
}

export function getVideoMetadata(src){
	return new Promise((resolve, reject) => {
		let video = document.createElement("video");

		video.addEventListener("loadedmetadata", (e) => {
			resolve({
				width: e.target.videoWidth,
				height: e.target.videoHeight,
				duration: e.target.duration, // in secoonds, floating point
			});
		});

		video.addEventListener("error", (e) => {
			reject();
		});

		video.src = src;
	});
}


/*
	Asset Object Validation Helpers

		For selected Assets that we've fetched from the API, the metadata we need for validation
		exists in the data, so reading is preferable to loading the entire file via img/video src.

		These can run synchronously after the data is fetched.

		The formats for different types of assets are inconsistent, so we use these functions
		to normalize the metadata structure
*/

export function getImageAssetMetadata(asset){
	let {
		width = 0,
		height = 0,
		filesize = 0,
	} = asset || {};
	return {
		width,
		height,
		filesize,
	};
}

export function getVideoAssetMetadata(asset){
	/*
		video Assets have a dimensions string that looks like "1280 x 720",
		instead of separate width and height properties
	*/
	let {
		dimensions = "",
		filesize = 0,
		duration = 0,
	} = asset || {};


	// If there's a problem with dimensions, parseInt should return NaN, which is falsy, and we map to 0
	let [width = 0, height = 0] = dimensions.split(" x ").map(x => parseInt(x) || 0);

	return {
		width,
		height,
		filesize,
		duration, // This API value seems like it may not be reliably accurate
	};
}
